/* eslint-disable consistent-return */

/* eslint-disable jsx-a11y/media-has-caption */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import PlayPauseButton from '../buttons/PlayPause';
import VolumeButton from '../buttons/Volume';
import Waves from '../icons/Waves';

import styles from '../../../styles/partials/audio-player.module.scss';

const propTypes = {
    audio: PropTypes.shape({
        url: PropTypes.string,
    }),
    formatTime: PropTypes.func,
    autoPlay: PropTypes.bool,
    disabled: PropTypes.bool,
    withVolume: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    audio: null,
    formatTime: (secondsTotal) => {
        const minutes = Math.floor(secondsTotal / 60);
        const seconds = Math.round(secondsTotal - minutes * 60);
        return [
            minutes < 10 ? `0${minutes}` : `${minutes}`,
            seconds < 10 ? `0${seconds}` : `${seconds}`,
        ].join(':');
    },
    autoPlay: false,
    disabled: false,
    withVolume: false,
    className: null,
};

function AudioPlayer({ audio, formatTime, autoPlay, disabled, withVolume, className }) {
    const themeClassName = useThemeClassName(styles);

    const audioUrl = useMemo(() => {
        const { url: defaultUrl = null, files = [] } = audio || {};
        const mp3File = (files || []).find((f) => f !== null && f.mime === 'audio/mpeg') || null;
        const { url: mp3Url = null } = mp3File || {};
        return mp3Url || defaultUrl;
    }, [audio]);

    const audioRef = useRef(null);
    const progressBarRef = useRef(null);

    const [playing, setPlaying] = useState(autoPlay);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [progress, setProgress] = useState(0);
    const [volume, setVolume] = useState(1);

    const togglePlayPause = useCallback(() => {
        if (!disabled) {
            setPlaying(!playing);
        }
    }, [setPlaying, playing, disabled]);

    const cycleVolume = useCallback(() => {
        setVolume((prev) => {
            if (prev === 0) {
                return 0.5;
            }
            if (prev === 0.5) {
                return 1;
            }
            return 0;
        });
    }, [setVolume]);

    // set duration and end playing
    useEffect(() => {
        const handleMetadataLoaded = () => {
            if (audioRef.current) {
                setDuration(audioRef.current.duration);
            }
        };
        const onEnded = () => {
            if (audioRef.current) {
                setPlaying(false);
            }
        };
        if (audioRef.current) {
            audioRef.current.addEventListener('ended', onEnded);
            audioRef.current.addEventListener('loadedmetadata', handleMetadataLoaded);
        }
        return () => {
            if (audioRef.current) {
                audioRef.current.removeEventListener('ended', onEnded);
                audioRef.current.removeEventListener('loadedmetadata', handleMetadataLoaded);
            }
        };
    }, [setDuration, setPlaying]);

    // set play/pause
    useEffect(() => {
        if (audioRef.current === null) {
            return;
        }
        if (!playing) {
            audioRef.current.pause();
        } else if (playing && (audioRef.current.paused || audioRef.current.currentTime === 0)) {
            try {
                audioRef.current.play();
                console.log('play me');
            } catch (e) {
                console.log('audioRef play error', e);
            }
        }
    }, [playing]);

    // update progress
    useEffect(() => {
        if (audioRef.current === null) {
            return;
        }
        const interval = setInterval(() => {
            if (audioRef.current !== null) {
                setCurrentTime(audioRef.current.currentTime);
                setProgress((audioRef.current.currentTime * 100) / audioRef.current.duration);
            }
        }, 100);

        return () => {
            clearInterval(interval);
        };
    }, [playing, setProgress, setCurrentTime]);

    // set volume
    useEffect(() => {
        if (audioRef.current === null) {
            return;
        }
        audioRef.current.volume = volume;
    }, [audioRef, volume]);

    // const onChangeProgress = (e) => {
    //     const inputValue = parseFloat(e.target.value || 0, 10);
    //     setProgress(inputValue);
    //     // setPlaying(true);
    //     if (audioRef.current) {
    //         const newTime = (inputValue * audioRef.current.duration) / 100;
    //         audioRef.current.currentTime = newTime;
    //     }
    // };

    return audioUrl !== null ? (
        <div
            className={classNames(styles.container, themeClassName, {
                [styles.playing]: playing,
                [styles.disabled]: disabled,
                [className]: className !== null,
            })}
        >
            <div className={styles.inner}>
                <div className={styles.progress} ref={progressBarRef} />
                <audio ref={audioRef} src={audioUrl} title={progress} />
                <div className={styles.controls}>
                    {!disabled ? (
                        <PlayPauseButton
                            onClick={togglePlayPause}
                            paused={!playing}
                            className={styles.button}
                        />
                    ) : null}

                    <Button onClick={togglePlayPause}>
                        <Waves
                            className={styles.waves}
                            title={`${formatTime(currentTime)
                                .split('')
                                .map((it) => <span>{it}</span>)}:${formatTime(duration)
                                .split('')
                                .map((it) => <span>{it}</span>)}`}
                        />
                    </Button>
                    {withVolume ? (
                        <VolumeButton
                            onClick={cycleVolume}
                            volume={volume}
                            className={styles.button}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    ) : null;
}

AudioPlayer.defaultProps = defaultProps;
AudioPlayer.propTypes = propTypes;

export default AudioPlayer;
